import React, { useState } from 'react';

interface CoachingNotesObject {
  what: string;
  why: string;
}

interface ExpandableCoachingNotesProps {
  notes: string | CoachingNotesObject;
  isOpen?: boolean;
}

/**
 * Expandable Coaching Notes Component
 * 🆕 JAN 29, 2026: Complete rewrite for object structure + markdown rendering
 * 
 * Features:
 * - Handles new {what, why} object structure
 * - Renders bold text properly (no asterisks)
 * - Renders proper bullet lists (<ul><li>)
 * - Uses "Today's Session" instead of "What we'll do"
 * - Backward compatible with legacy string format
 */
export const ExpandableCoachingNotes: React.FC<ExpandableCoachingNotesProps> = ({ 
  notes, 
  isOpen = false 
}) => {
  const [expanded, setExpanded] = useState(true);

  if (!notes) return null;

  // 🆕 JAN 29, 2026: Handle new object structure {what, why}
  const isObjectStructure = typeof notes === 'object' && 'what' in notes && 'why' in notes;

  if (isObjectStructure) {
    const { what, why } = notes as CoachingNotesObject;

    // Parse why bullets (can be • or - at start of lines)
    const whyLines = why.split('\n').filter(line => line.trim().length > 0);
    const whyBullets = whyLines
      .map(line => line.trim())
      .filter(line => line.startsWith('•') || line.startsWith('-'))
      .map(line => line.replace(/^[•\-]\s*/, '').trim());

    return (
      <div className="mb-6">
        {/* Today's Session Section (What) */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mb-4">
          <div className="flex items-start gap-2 mb-2">
            <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h4 className="font-semibold text-blue-900">Today's Session</h4>
          </div>
          <p className="text-sm text-blue-800 leading-relaxed">{what}</p>
        </div>

        {/* Why Section */}
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-start gap-2 mb-2">
            <svg className="w-5 h-5 text-[#1F6C4F] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h4 className="font-semibold text-[#1F6C4F]">Why</h4>
          </div>
          {whyBullets.length > 0 ? (
            <ul className="text-sm text-gray-700 leading-relaxed space-y-1 list-disc list-inside">
              {whyBullets.map((bullet, idx) => (
                <li key={idx}>{bullet}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-700 leading-relaxed">{why}</p>
          )}
        </div>
      </div>
    );
  }

  // 🔧 Legacy string format - parse markdown and render properly
  const notesStr = typeof notes === 'string' ? notes : JSON.stringify(notes, null, 2);
  if (notesStr.trim().length === 0) return null;

  // Check for markdown structure headers
  const hasTodaysSession = notesStr.includes("**Today's Session**");
  const hasWhatWellDo = notesStr.includes("**What we'll do**");
  const hasWhy = notesStr.includes("**Why**");

  if ((hasTodaysSession || hasWhatWellDo) && hasWhy) {
    // Split into sections
    const parts = notesStr.split("**Why**");
    let whatText = parts[0] || "";
    let whyText = parts[1]?.trim() || "";

    // Remove header markers and clean up
    whatText = whatText
      .replace("**Today's Session**", "")
      .replace("**What we'll do**", "")
      .replace(/\*\*/g, '')  // Remove all remaining ** markers
      .trim();

    whyText = whyText.replace(/\*\*/g, '').trim();

    // Parse why bullets
    const whyLines = whyText.split('\n').filter(line => line.trim().length > 0);
    const whyBullets = whyLines
      .map(line => line.trim())
      .filter(line => line.startsWith('•') || line.startsWith('-'))
      .map(line => line.replace(/^[•\-]\s*/, '').trim());

    return (
      <div className="mb-6">
        {/* Today's Session Section */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mb-4">
          <div className="flex items-start gap-2 mb-2">
            <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h4 className="font-semibold text-blue-900">Today's Session</h4>
          </div>
          <p className="text-sm text-blue-800 leading-relaxed">{whatText}</p>
        </div>

        {/* Why Section */}
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-start gap-2 mb-2">
            <svg className="w-5 h-5 text-[#1F6C4F] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h4 className="font-semibold text-[#1F6C4F]">Why</h4>
          </div>
          {whyBullets.length > 0 ? (
            <ul className="text-sm text-gray-700 leading-relaxed space-y-1 list-disc list-inside">
              {whyBullets.map((bullet, idx) => (
                <li key={idx}>{bullet}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-700 leading-relaxed">{whyText}</p>
          )}
        </div>
      </div>
    );
  }

  // Final fallback: Plain text (very old format)
  return (
    <details 
      className="mb-6"
      open={expanded}
      onToggle={(e) => setExpanded((e.target as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-2">
          <span className="text-gray-400">{expanded ? '▼' : '▶'}</span>
          <span className="font-semibold text-gray-900">Coaching Notes</span>
        </div>
      </summary>
      
      <div className="mt-2 p-4 bg-white rounded-lg border border-gray-200 border-t-0 rounded-t-none">
        <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
          {notesStr.split('\n\n').map((paragraph, index) => (
            <p key={index} className={index > 0 ? 'mt-3' : ''}>
              {paragraph.replace(/\*\*/g, '')}
            </p>
          ))}
        </div>
      </div>
    </details>
  );
};

